import React, { useEffect, useMemo, useRef, useState } from 'react';
import InventoryLayout from './InventoryLayout';

const InventoryPickTablet = () => {
  const [vognNr, setVognNr] = useState('');
  const [vognSuggestions, setVognSuggestions] = useState([]);
  const [scanValue, setScanValue] = useState('');
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [isSearchingItems, setIsSearchingItems] = useState(false);
  const [lines, setLines] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickData, setQuickData] = useState({ name: '', default_price: '', default_partner_id: '', min_qty: '', location: '' });
  const [partners, setPartners] = useState([]);
  const [scanCameraActive, setScanCameraActive] = useState(false);
  const [scanCameraError, setScanCameraError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [step, setStep] = useState(1);
  const vognInputRef = useRef(null);
  const inputRef = useRef(null);
  const scanVideoRef = useRef(null);
  const scanStreamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const scanDetectorRef = useRef(null);
  const textDetectorRef = useRef(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  useEffect(() => {
    if (step === 1) {
      setTimeout(() => vognInputRef.current?.focus(), 50);
    }
    if (step === 2) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [step]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    handleFullscreenChange();
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const res = await fetch('/api/b2b/partners', { headers });
        const data = await res.json();
        if (res.ok) setPartners(Array.isArray(data) ? data : []);
      } catch (_) {
        setPartners([]);
      }
    };
    loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = useMemo(
    () => lines.reduce((sum, line) => sum + line.qty * line.unit_price, 0),
    [lines]
  );
  const kioskInputProps = useMemo(() => (
    isFullscreen
      ? {
          autoComplete: 'new-password',
          autoCorrect: 'off',
          autoCapitalize: 'off',
          spellCheck: false,
          inputMode: 'text',
          'aria-autocomplete': 'none',
          'data-form-type': 'other',
          'data-lpignore': 'true',
          'data-1p-ignore': 'true'
        }
      : {}
  ), [isFullscreen]);

  const resetScanFocus = () => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  const handleScanCode = async (code) => {
    setError('');
    setMessage('');
    if (!code) return;
    try {
      const res = await fetch(`/api/inventory/lookup?code=${encodeURIComponent(code)}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Vare ikke fundet');
      addItemToLines(data);
      if (navigator.vibrate) navigator.vibrate(50);
      setScanValue('');
      setItemSuggestions([]);
    } catch (err) {
      setError(err.message || 'Vare ikke fundet');
      setShowQuickCreate(true);
    } finally {
      resetScanFocus();
    }
  };

  const extractSkuCandidate = (text) => {
    if (!text) return '';
    const digitsOnly = text.replace(/\D+/g, '');
    if (digitsOnly.length >= 5) {
      return digitsOnly;
    }
    const normalized = text.toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
    if (!normalized) return '';
    const candidates = normalized.split(/\s+/).filter((entry) => entry.length >= 3);
    return candidates[0] || '';
  };

  useEffect(() => {
    let isActive = true;
    if (step !== 1) {
      setVognSuggestions([]);
      return undefined;
    }
    if (vognNr.trim().length < 2) {
      setVognSuggestions([]);
      return undefined;
    }
    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch(`/api/biler/offentlig-suggest?lookup=${encodeURIComponent(vognNr.trim())}`, {
          headers
        });
        const data = await res.json();
        if (!isActive) return;
        if (res.ok && Array.isArray(data)) {
          setVognSuggestions(data);
        } else {
          setVognSuggestions([]);
        }
      } catch (_) {
        if (isActive) setVognSuggestions([]);
      }
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [headers, vognNr]);

  useEffect(() => {
    let isActive = true;
    if (step !== 2) {
      setItemSuggestions([]);
      setIsSearchingItems(false);
      return undefined;
    }
    const query = scanValue.trim();
    if (query.length < 2) {
      setItemSuggestions([]);
      setIsSearchingItems(false);
      return undefined;
    }
    setIsSearchingItems(true);
    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch(`/api/inventory/items/search?query=${encodeURIComponent(query)}`, { headers });
        const data = await res.json();
        if (!isActive) return;
        if (res.ok && Array.isArray(data)) {
          setItemSuggestions(data);
        } else {
          setItemSuggestions([]);
        }
      } catch (_) {
        if (isActive) setItemSuggestions([]);
      } finally {
        if (isActive) setIsSearchingItems(false);
      }
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [headers, scanValue]);

  const addItemToLines = (item) => {
    setLines((prev) => {
      const existing = prev.find((line) => line.item_id === item.id);
      if (existing) {
        return prev.map((line) =>
          line.item_id === item.id ? { ...line, qty: line.qty + 1 } : line
        );
      }
      return [
        ...prev,
        {
          item_id: item.id,
          name: item.name,
          qty: 1,
          unit_price: Number(item.default_price || 0)
        }
      ];
    });
  };

  const handleScanSubmit = async (e) => {
    e.preventDefault();
    const code = scanValue.trim();
    await handleScanCode(code);
  };

  const handleQtyChange = (itemId, delta) => {
    setLines((prev) =>
      prev
        .map((line) => (line.item_id === itemId ? { ...line, qty: Math.max(1, line.qty + delta) } : line))
    );
  };

  const handleLineUpdate = (itemId, updates) => {
    setLines((prev) => prev.map((line) => (line.item_id === itemId ? { ...line, ...updates } : line)));
  };

  const handleRemoveLine = (itemId) => {
    setLines((prev) => prev.filter((line) => line.item_id !== itemId));
  };

  const handleSubmitPick = async () => {
    setError('');
    setMessage('');
    if (!vognNr.trim()) {
      setError('Angiv vogn nr.');
      return;
    }
    if (lines.length === 0) {
      setError('Ingen varer i kurven.');
      return;
    }
    try {
      const res = await fetch('/api/inventory/pick', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vogn_nr: vognNr.trim(),
          lines: lines.map((line) => ({
            item_id: line.item_id,
            qty: line.qty,
            unit_price: line.unit_price
          }))
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke gemme pluk');
      setMessage('Pluk registreret og lager opdateret.');
      setLines([]);
      setVognNr('');
      setStep(1);
    } catch (err) {
      setError(err.message || 'Kunne ikke gemme pluk');
    } finally {
      if (step === 2) resetScanFocus();
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch (err) {
      setError('Kunne ikke skifte til fuld skærm.');
    }
  };

  const handleSelectVogn = (value) => {
    setVognNr(value);
    setVognSuggestions([]);
    setError('');
    setMessage('');
    setStep(2);
  };

  const handleConfirmVogn = () => {
    if (!vognNr.trim()) {
      setError('Angiv vogn nr.');
      return;
    }
    setError('');
    setMessage('');
    setStep(2);
  };

  const handleBackToVogn = () => {
    setStep(1);
  };

  const handleGoToReview = () => {
    if (lines.length === 0) {
      setError('Ingen varer i kurven.');
      return;
    }
    setError('');
    setMessage('');
    setStep(3);
  };

  const handleSelectItem = (item) => {
    addItemToLines(item);
    setScanValue('');
    setItemSuggestions([]);
    setShowQuickCreate(false);
    setError('');
    setMessage('');
    resetScanFocus();
  };

  const handleQuickCreate = async () => {
    if (!quickData.name.trim()) {
      setError('Angiv navn på varen.');
      return;
    }
    try {
      const res = await fetch('/api/inventory/items', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: quickData.name,
          default_price: quickData.default_price || 0,
          default_partner_id: quickData.default_partner_id || null,
          min_qty: quickData.min_qty || 0,
          location: quickData.location || null,
          sku: '',
          barcode: scanValue.trim(),
          stock_qty: 0
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke oprette vare');
      setShowQuickCreate(false);
      setQuickData({ name: '', default_price: '', default_partner_id: '', min_qty: '', location: '' });
      setScanValue('');
      setItemSuggestions([]);
      setMessage('Vare oprettet. Scan igen for at tilføje.');
    } catch (err) {
      setError(err.message || 'Kunne ikke oprette vare');
    } finally {
      resetScanFocus();
    }
  };

  useEffect(() => {
    if (!scanCameraActive) return undefined;
    let cancelled = false;
    const startScan = async () => {
      if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        setScanCameraError('Kamera er ikke tilgængeligt på denne enhed.');
        setScanCameraActive(false);
        return;
      }
      const hasBarcodeDetector = 'BarcodeDetector' in window;
      const hasTextDetector = 'TextDetector' in window;
      if (!hasBarcodeDetector && !hasTextDetector) {
        setScanCameraError('Browseren understøtter ikke stregkode- eller tekstscanning.');
        setScanCameraActive(false);
        return;
      }
      try {
        setScanCameraError('');
        scanDetectorRef.current = hasBarcodeDetector
          ? new window.BarcodeDetector({ formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_e', 'upc_a'] })
          : null;
        textDetectorRef.current = hasTextDetector ? new window.TextDetector() : null;
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        scanStreamRef.current = stream;
        if (scanVideoRef.current) {
          scanVideoRef.current.srcObject = stream;
          await scanVideoRef.current.play();
        }
        scanIntervalRef.current = window.setInterval(async () => {
          if (!scanVideoRef.current) return;
          try {
            if (scanDetectorRef.current) {
              const barcodes = await scanDetectorRef.current.detect(scanVideoRef.current);
              if (barcodes.length > 0) {
                const value = barcodes[0]?.rawValue || '';
                if (value) {
                  setScanCameraActive(false);
                  await handleScanCode(value);
                  return;
                }
              }
            }
            if (textDetectorRef.current) {
              const texts = await textDetectorRef.current.detect(scanVideoRef.current);
              for (const item of texts) {
                const candidate = extractSkuCandidate(item.rawValue || item.text || '');
                if (candidate) {
                  setScanCameraActive(false);
                  await handleScanCode(candidate);
                  return;
                }
              }
            }
          } catch (_) {
            // ignore scan errors
          }
        }, 400);
      } catch (err) {
        setScanCameraError('Kunne ikke starte kameraet.');
        setScanCameraActive(false);
      }
    };
    startScan();

    return () => {
      cancelled = true;
      if (scanIntervalRef.current) {
        window.clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      if (scanStreamRef.current) {
        scanStreamRef.current.getTracks().forEach((track) => track.stop());
        scanStreamRef.current = null;
      }
      textDetectorRef.current = null;
    };
  }, [scanCameraActive, headers]);

  useEffect(() => {
    if (step !== 2 && scanCameraActive) {
      setScanCameraActive(false);
    }
  }, [scanCameraActive, step]);

  useEffect(() => {
    if (!scanCameraActive && scanIntervalRef.current) {
      window.clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (!scanCameraActive && scanStreamRef.current) {
      scanStreamRef.current.getTracks().forEach((track) => track.stop());
      scanStreamRef.current = null;
    }
  }, [scanCameraActive]);

  return (
    <InventoryLayout
      title="Lager - pluk"
      description={isFullscreen ? '' : 'Scan varer og registrer forbrug direkte på vognen.'}
      headerAction={(
        <button
          type="button"
          onClick={toggleFullscreen}
          className="inline-flex items-center justify-center p-2 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800"
          aria-label={isFullscreen ? 'Forlad kiosk' : 'Kiosk mode'}
          title={isFullscreen ? 'Forlad kiosk' : 'Kiosk mode'}
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
            <path
              d="M4.5 12.5 7 7.5h10l2.5 5M4 12.5h16v4H4zM7 16.5v2.5M17 16.5v2.5M6 12.5h12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    >
      <div className="mb-4">
        <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
          <div
            className="h-full bg-falck-blue transition-all duration-300"
            style={{ width: `${((step - 1) / 2) * 100}%` }}
          />
        </div>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      {message && <div className="mb-3 p-3 bg-green-100 text-green-700 rounded">{message}</div>}

      {step === 1 && (
        <div className="max-w-2xl">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Vogn nr.</label>
            <div className="relative mt-1">
              <input
                ref={vognInputRef}
                value={vognNr}
                onChange={(e) => setVognNr(e.target.value)}
                name="inventory-vehicle"
                className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
                placeholder="Søg internt nr., nummerplade eller stelnummer..."
                {...kioskInputProps}
              />
              {vognSuggestions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow">
                  {vognSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      type="button"
                      onClick={() => handleSelectVogn(String(suggestion.internt_nummer || ''))}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {suggestion.internt_nummer || 'Ukendt vogn'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {suggestion.nummerplade || 'Ingen nummerplade'}
                        {suggestion.station_navn ? ` • ${suggestion.station_navn}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleConfirmVogn}
                disabled={!vognNr.trim()}
                className="px-5 py-2 rounded-md bg-falck-blue text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                Næste
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4">
                <form onSubmit={handleScanSubmit} autoComplete={isFullscreen ? 'off' : 'on'}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Scan (EAN/Varenummer)</label>
                  <div className="relative mt-1">
                    <input
                      ref={inputRef}
                      value={scanValue}
                      onChange={(e) => setScanValue(e.target.value)}
                      name="inventory-scan"
                      className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 text-lg"
                      placeholder="Scan eller søg på navn/vare nummer..."
                      {...kioskInputProps}
                    />
                    {isSearchingItems && (
                      <div className="absolute right-3 top-3 text-xs text-gray-400">Søger...</div>
                    )}
                    {itemSuggestions.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow max-h-60 overflow-y-auto">
                        {itemSuggestions.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleSelectItem(item)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">{item.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {item.sku ? `Vare nr.: ${item.sku}` : 'Ingen varenr.'}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 items-center">
                    <button
                      type="button"
                      onClick={() => setScanCameraActive((prev) => !prev)}
                      className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm"
                    >
                      {scanCameraActive ? 'Stop scanning' : 'Scan med kamera'}
                    </button>
                    {scanCameraError && <span className="text-xs text-red-600">{scanCameraError}</span>}
                  </div>
                  {scanCameraActive && (
                    <div className="mt-3">
                      <video ref={scanVideoRef} className="w-full rounded-md border border-gray-200 dark:border-gray-700" />
                      <p className="text-xs text-gray-500 mt-1">Peg kameraet på stregkoden.</p>
                    </div>
                  )}
                </form>
              </div>

              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                  <h2 className="font-semibold text-gray-800 dark:text-gray-100">Kurv</h2>
                </div>
                {lines.length === 0 ? (
                  <div className="p-4 text-gray-500">Ingen varer tilføjet endnu.</div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {lines.map((line) => (
                      <div key={line.item_id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{line.name}</p>
                          <p className="text-sm text-gray-500">Pris: {line.unit_price} kr</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleQtyChange(line.item_id, -1)}
                            className="px-2 py-1 border rounded"
                          >
                            -
                          </button>
                          <span className="min-w-[2rem] text-center">{line.qty}</span>
                          <button
                            type="button"
                            onClick={() => handleQtyChange(line.item_id, 1)}
                            className="px-2 py-1 border rounded"
                          >
                            +
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveLine(line.item_id)}
                            className="text-red-600 text-sm"
                          >
                            Fjern
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">Total</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{total.toFixed(2)} kr</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleBackToVogn}
                  className="flex-1 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 py-3 rounded-md font-semibold"
                >
                  Tilbage
                </button>
                <button
                  type="button"
                  onClick={handleGoToReview}
                  disabled={lines.length === 0}
                  className="flex-1 bg-falck-blue text-white py-3 rounded-md font-semibold hover:bg-blue-700 disabled:opacity-50"
                >
                  Næste
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Valgt køretøj</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{vognNr || '-'}</p>
            </div>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="text-sm font-semibold text-falck-blue hover:underline"
            >
              Tilbage til pluk
            </button>
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="font-semibold text-gray-800 dark:text-gray-100">Gennemgang af ordre</h2>
            </div>
            {lines.length === 0 ? (
              <div className="p-4 text-gray-500">Ingen varer tilføjet endnu.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vare
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Antal
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pris
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Linje
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                    {lines.map((line) => (
                      <tr key={line.item_id}>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {line.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          <input
                            type="number"
                            min="1"
                            value={line.qty}
                            onChange={(e) => {
                              const next = parseInt(e.target.value, 10);
                              handleLineUpdate(line.item_id, { qty: Number.isFinite(next) && next > 0 ? next : 1 });
                            }}
                            className="w-20 border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={Number.isFinite(line.unit_price) ? line.unit_price : 0}
                            onChange={(e) => {
                              const next = Number(e.target.value);
                              handleLineUpdate(line.item_id, { unit_price: Number.isFinite(next) ? next : 0 });
                            }}
                            className="w-24 border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 text-right">
                          {(line.qty * line.unit_price).toFixed(2)} kr
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200"
            >
              Tilbage
            </button>
            <div className="text-right">
              <p className="text-sm text-gray-600 dark:text-gray-300">Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{total.toFixed(2)} kr</p>
            </div>
            <button
              type="button"
              onClick={handleSubmitPick}
              className="px-6 py-3 rounded-md bg-falck-blue text-white font-semibold hover:bg-blue-700"
            >
              Godkend
            </button>
          </div>
        </div>
      )}

      {showQuickCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Opret vare hurtigt</h3>
            <div className="space-y-3">
              <input
                placeholder="Navn"
                value={quickData.name}
                onChange={(e) => setQuickData({ ...quickData, name: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
                  {...kioskInputProps}
              />
              <input
                type="number"
                step="0.01"
                placeholder="Pris"
                value={quickData.default_price}
                onChange={(e) => setQuickData({ ...quickData, default_price: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
                  {...kioskInputProps}
              />
              <select
                value={quickData.default_partner_id}
                onChange={(e) => setQuickData({ ...quickData, default_partner_id: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              >
                <option value="">Vælg leverandør</option>
                {partners.map((partner) => (
                  <option key={partner.id} value={partner.id}>{partner.name}</option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Min antal"
                value={quickData.min_qty}
                onChange={(e) => setQuickData({ ...quickData, min_qty: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
                  {...kioskInputProps}
              />
              <input
                placeholder="Lokation"
                value={quickData.location}
                onChange={(e) => setQuickData({ ...quickData, location: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
                  {...kioskInputProps}
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setShowQuickCreate(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md"
              >
                Annuller
              </button>
              <button
                type="button"
                onClick={handleQuickCreate}
                className="px-4 py-2 bg-falck-blue text-white rounded-md"
              >
                Opret vare
              </button>
            </div>
          </div>
        </div>
      )}
    </InventoryLayout>
  );
};

export default InventoryPickTablet;
